import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getAccessToken } from '../../lib/api/client.js';
import { Badge, Empty, ErrorBanner, SectionHead, Segmented, SubHead, TableSkeleton, EditorHead } from '../../components/ui/Primitives.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { Icon } from '../../components/ui/Icon.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { toastError } from '../../components/ui/Toast.js';

/**
 * Scheduled reports (0146, owner 1 October 2026: "automated reports that can be scheduled to send
 * at certain intervals ... extension uptime, campaigns, cdrs, anything we want to report on").
 * A PDF summary with a CSV per section, emailed to people on the account -- never typed addresses
 * -- who may see everything in it.
 */

type Frequency = 'daily' | 'weekly' | 'monthly';
type QaGroup = 'agent' | 'department' | 'queue' | 'campaign';
interface Schedule {
  id: string; name: string; sections: string[]; frequency: Frequency; send_hour: number; weekday: number;
  recipients: string[]; enabled: boolean; last_period: string | null; last_sent_at: string | null; last_note: string | null;
  /** What sections need beyond the period (0148): how the Auto QA report is grouped. */
  options?: { qa_group?: QaGroup };
}
const QA_GROUPS: ReadonlyArray<readonly [QaGroup, string]> = [
  ['agent', 'Person'], ['department', 'Department'], ['queue', 'Queue'], ['campaign', 'Campaign'],
];
interface User { id: string; name: string; email: string; sections: string[] }
interface SectionInfo { key: string; title: string; mine: boolean }
interface List { data: Schedule[]; users: User[]; sections: SectionInfo[] }

/** What each section holds, said where it is chosen. */
const WHAT: Record<string, string> = {
  calls: 'Totals, each extension’s calls, and every call in a spreadsheet.',
  availability: 'Each phone’s uptime and outages.',
  queues: 'Callers, answered, abandoned and waits, per queue.',
  agents: 'Each agent’s queue calls, misses, talk time and time logged on.',
  campaigns: 'Each campaign’s calls, connections, leads left and cost.',
  spend: 'What calls cost, by extension.',
  auto_qa: 'Calls checked, marks and passes, with the full Auto QA report attached.',
};
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

/** When it goes, in words: "Every Monday at 07:00, for the week before". */
function when(s: Pick<Schedule, 'frequency' | 'send_hour' | 'weekday'>): string {
  if (s.frequency === 'daily') return `Every day at ${hh(s.send_hour)}, for the day before`;
  if (s.frequency === 'weekly') return `Every ${DAYS[s.weekday - 1]} at ${hh(s.send_hour)}, for the week before`;
  return `On the 1st at ${hh(s.send_hour)}, for the month before`;
}

export function ScheduledReportsTab({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/report-schedules`;
  const list = useQuery({ queryKey: ['report-schedules', tenantId], queryFn: () => api.get<List>(base) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['report-schedules', tenantId] });
  const [editing, setEditing] = useState<Partial<Schedule> | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const remove = useMutation({ mutationFn: (id: string) => api.delete(`${base}/${id}`), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: (s: Schedule) => api.patch(`${base}/${s.id}`, { enabled: !s.enabled }), onSuccess: refresh });
  const sendNow = useMutation({
    mutationFn: (id: string) => api.post<{ from: string; to: string; sent: number; left_out: string[] }>(`${base}/${id}/send-now`, {}),
    onSuccess: (r) => setNote(`Sent the report for ${r.from === r.to ? r.from : `${r.from} to ${r.to}`} to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}.`
      + (r.left_out.length ? ` Left out: ${r.left_out.join('; ')}.` : '')),
  });
  /** The PDF of the last period, opened in a new tab: fetched with the session, as a blob. */
  const preview = async (id: string) => {
    try {
      const res = await fetch(`/api${base}/${id}/preview`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) {
        const p = await res.json().catch(() => null) as { detail?: string; title?: string } | null;
        throw new Error(p?.detail ?? p?.title ?? `The preview could not be built (${res.status}).`);
      }
      window.open(URL.createObjectURL(await res.blob()), '_blank', 'noopener');
    } catch (e) { toastError(e); }
  };

  const data = list.data;
  const users = data?.users ?? [];
  const titleOf = (k: string) => data?.sections.find((x) => x.key === k)?.title ?? k;
  const nameOf = (id: string) => users.find((u) => u.id === id)?.name ?? 'someone no longer on the account';

  if (editing && data) {
    return (
      <>
        <EditorHead back="Scheduled reports" onBack={() => setEditing(null)} title={editing.id ? (editing.name ?? 'Report') : 'New scheduled report'} />
        <Editor tenantId={tenantId} base={base} initial={editing} list={data} onDone={() => { setEditing(null); refresh(); }} />
      </>
    );
  }

  return (
    <>
      <SectionHead title="Scheduled reports"
        sub="Reports that build and email themselves: a PDF summary, with the full figures as spreadsheets, to people on this account."
        action={(
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" disabled={!data}
            onClick={() => setEditing({ name: '', sections: [], frequency: 'weekly', send_hour: 7, weekday: 1, recipients: [], enabled: true })}>
            <Icon name="plus" size={13} /> New report
          </button>
        )} />
      {list.error && <ErrorBanner error={list.error} />}
      {note && <p className="arc-muted" style={{ fontSize: 12.5, margin: '0 0 12px' }}>{note}</p>}
      {sendNow.error && <ErrorBanner error={sendNow.error} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>Report</th><th>Contains</th><th>When</th><th>To</th><th>Last sent</th><th /></tr>
            </thead>
            {!data ? <TableSkeleton cols={6} /> : (
              <tbody>
                {data.data.map((s) => (
                  <tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.6 }}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{s.name}</span>
                      {!s.enabled && <span style={{ marginLeft: 8 }}><Badge tone="muted">paused</Badge></span>}
                      {s.last_note && (
                        <div style={{ fontSize: 12, color: 'var(--arc-warn)', marginTop: 2, maxWidth: 320 }}>{s.last_note}</div>
                      )}
                    </td>
                    <td className="arc-muted" style={{ maxWidth: 240 }}>{s.sections.map(titleOf).join(', ')}</td>
                    <td className="arc-muted">{when(s)}</td>
                    <td className="arc-muted" style={{ maxWidth: 220 }}>{s.recipients.map(nameOf).join(', ')}</td>
                    <td className="arc-muted">{s.last_sent_at ? new Date(s.last_sent_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—'}</td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => void preview(s.id)}>Preview</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={sendNow.isPending}
                          onClick={() => sendNow.mutate(s.id)}>Send now</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => toggle.mutate(s)}>{s.enabled ? 'Pause' : 'Resume'}</button>
                        <RowAction kind="edit" label={`Edit ${s.name}`} onClick={() => setEditing(s)} />
                        <RowAction kind="delete" label={`Delete ${s.name}`} onClick={() => {
                          void confirmAction({ title: `Stop sending "${s.name}"?`, action: 'Delete', danger: true })
                            .then((ok) => { if (ok) remove.mutate(s.id); });
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {data && data.data.length === 0 && (
          <Empty icon="clock" title="No scheduled reports"
            hint="Choose what goes in a report, how often, and who on the account receives it." />
        )}
      </div>
    </>
  );
}

function Editor({ base, initial, list, onDone }: {
  tenantId: string; base: string; initial: Partial<Schedule>; list: List; onDone: () => void;
}) {
  const [name, setName] = useState(initial.name ?? '');
  const [sections, setSections] = useState<string[]>(initial.sections ?? []);
  const [frequency, setFrequency] = useState<Frequency>(initial.frequency ?? 'weekly');
  const [hour, setHour] = useState(initial.send_hour ?? 7);
  const [weekday, setWeekday] = useState(initial.weekday ?? 1);
  const [recipients, setRecipients] = useState<string[]>(initial.recipients ?? []);
  const [qaGroup, setQaGroup] = useState<QaGroup>(initial.options?.qa_group ?? 'agent');

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), sections, frequency, send_hour: hour, weekday, recipients,
        options: sections.includes('auto_qa') ? { qa_group: qaGroup } : {} };
      return initial.id ? api.patch(`${base}/${initial.id}`, body) : api.post(base, { ...body, enabled: true });
    },
    onSuccess: onDone,
  });

  /** What a person could not see of the sections chosen: they cannot be sent the report. */
  const missing = (u: User) => sections.filter((k) => !u.sections.includes(k));
  const title = (k: string) => list.sections.find((x) => x.key === k)?.title ?? k;
  const blocked = list.users.filter((u) => recipients.includes(u.id) && missing(u).length > 0);
  const why = !name.trim() ? 'Give the report a name.'
    : sections.length === 0 ? 'Choose at least one section.'
      : recipients.length === 0 ? 'Choose who receives it.'
        : blocked.length ? `${blocked.map((u) => u.name).join(', ')} cannot see everything in it.` : null;

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (!why) save.mutate(); }}>
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0, maxWidth: 420 }}>
          <label className="arc-label" htmlFor="rs-name">Name</label>
          <input id="rs-name" className="arc-input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
            placeholder="Weekly operations" />
          <p className="arc-hint">The title on the PDF and the email’s subject.</p>
        </div>
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="Each section is a page of the PDF and a spreadsheet of its own.">What it contains</SubHead>
        <div className="arc-rs-sections">
          {list.sections.map((x) => {
            const on = sections.includes(x.key);
            return (
              <label key={x.key} className={`arc-rs-section${on ? ' is-on' : ''}${x.mine ? '' : ' is-off'}`}>
                <input type="checkbox" checked={on} disabled={!x.mine && !on}
                  onChange={(e) => setSections(e.target.checked ? [...sections, x.key] : sections.filter((k) => k !== x.key))} />
                <span>
                  <strong>{x.title}</strong>
                  <span className="arc-muted">{x.mine ? WHAT[x.key] : 'You cannot see this yourself, so cannot send it.'}</span>
                </span>
              </label>
            );
          })}
        </div>
        {/* How the Auto QA report is grouped: the old Auto QA scheduler's choice, kept (0148). */}
        {sections.includes('auto_qa') && (
          <div className="arc-field" style={{ margin: '12px 0 0', maxWidth: 240 }}>
            <label className="arc-label" htmlFor="rs-qa-group">Auto QA grouped by</label>
            <select id="rs-qa-group" className="arc-select" value={qaGroup} onChange={(e) => setQaGroup(e.target.value as QaGroup)}>
              {QA_GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="In the account’s own time zone. Each period is sent once.">When</SubHead>
        <div className="arc-row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <span className="arc-label">How often</span>
            <Segmented label="How often" value={frequency} onChange={setFrequency}
              options={[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]} />
          </div>
          {frequency === 'weekly' && (
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rs-day">On</label>
              <select id="rs-day" className="arc-select" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                {DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
              </select>
            </div>
          )}
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="rs-hour">At</label>
            <select id="rs-hour" className="arc-select" value={hour} onChange={(e) => setHour(Number(e.target.value))}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
          </div>
        </div>
        <p className="arc-hint" style={{ marginBottom: 0 }}>{when({ frequency, send_hour: hour, weekday })}.</p>
      </div>

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <SubHead sub="People on this account only, and only those who may see everything in the report in the portal.">To</SubHead>
        <div className="arc-rs-people">
          {list.users.map((u) => {
            const lacks = missing(u);
            const on = recipients.includes(u.id);
            return (
              <label key={u.id} className="arc-row" style={{ gap: 8, fontSize: 13, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={on}
                  onChange={(e) => setRecipients(e.target.checked ? [...recipients, u.id] : recipients.filter((x) => x !== u.id))} />
                <span>
                  {u.name} <span className="arc-subtle">{u.email}</span>
                  {lacks.length > 0 && (
                    <span style={{ display: 'block', fontSize: 12, color: on ? 'var(--arc-danger)' : 'var(--arc-fg-3)' }}>
                      Cannot see {lacks.map(title).join(', ')}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
          {list.users.length === 0 && <p className="arc-muted" style={{ margin: 0 }}>Nobody on this account can sign in to the portal yet.</p>}
        </div>
      </div>

      {save.error && <ErrorBanner error={save.error} />}
      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={!!why || save.isPending}>
          {save.isPending ? 'Saving…' : initial.id ? 'Save changes' : 'Create report'}
        </button>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        {why && <span className="arc-muted" style={{ fontSize: 12.5 }}>{why}</span>}
      </div>
    </form>
  );
}
